/*
 * The marketing site's origin, and the pages of it this dashboard links to.
 *
 * A property of the deployment rather than of the company: a UAT dashboard
 * belongs to a UAT site, the same reasoning `VITE_INFERENCE_URL` is written
 * down for, and the same reasoning the marketing site applies in the other
 * direction to reach this dashboard.
 *
 * Defaulted to the local Astro dev server rather than to production. A default
 * of `https://maxlayer.cloud` would keep every build working and make a
 * forgotten variable silent, and silent is how a UAT dashboard ends up sending
 * customers to production terms. Pointing at localhost fails where somebody can
 * see it.
 *
 * Its own module rather than a second export from `legal.ts`, which is about
 * the four documents an account is bound by — the documentation is not one of
 * those, and a file that holds both stops predicting its own contents.
 */
import { DEFAULT_LOCALE } from "../i18n/locale";

export const SITE_URL = (
  (import.meta.env.VITE_SITE_URL as string | undefined) ?? "http://localhost:4321"
).replace(/\/+$/, "");

/**
 * The documentation, which lives on the marketing site rather than here.
 *
 * Deliberately the hub and not a deep link: the rail item is "the docs" rather
 * than any particular page, and the hub is the one URL that keeps meaning that
 * as the guides are rewritten.
 */
export const DOCS_URL = `${SITE_URL}/docs`;

/**
 * A page on the marketing site, in the dashboard's current language.
 *
 * The site keeps English at its old URLs and every other language under a
 * prefix, and the documentation is translated. A link built from `SITE_URL`
 * alone lands an Indonesian session on English docs — the site's own
 * redirect only catches a visitor it can place by cookie or by country, and
 * an account whose language was set on the profile has neither. Say the
 * language in the URL instead; the site answers English for a page that has
 * no translation, so this is safe for any path.
 */
export function siteUrl(path: string, locale: string): string {
  const prefix = locale === DEFAULT_LOCALE ? "" : `/${locale}`;
  return `${SITE_URL}${prefix}${path}`;
}
